import React, { useEffect, useRef } from 'react';
import { useQuery } from "@apollo/client";
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';

import { publicKeys } from '../../../config/stripeConfig';
import { useAppState } from "../../contexts/AppContext";
import { GET_COUNTRY_CODE } from '../../includes/queries';

type Props = {
    children?: React.ReactNode,
};

const StripeLoader: React.FC<Props> = ({ children }) => {
    const stripeKey = useRef('none');
    const [appState, setAppState] = useAppState();

    const { data, loading, error } = useQuery(GET_COUNTRY_CODE, {
        context: {
            clientName: "corporate",
        }
    });

    // Latched: once resolved, a later refetch must not blank prices that are already correct.
    useEffect(() => {
        if (!loading) {
            // A failed lookup settles too (never finds a match), so preserve a prior true from the match effect below.
            setAppState((prev: any) => ({
                hasLoadedCountryCodes: true,
                isCountryLookupSettled: !!error || prev.isCountryLookupSettled,
            }));
        }
    }, [loading, error, setAppState]);

    useEffect(() => {
        const countryCode = appState.selectedPlan?.CountryCode ||
            appState.user?.CountryCodeFromIp ||
            appState.country.codeFromIP;

        if (data?.countryCodes && countryCode) {
            setAppState({
                countryCodes: data?.countryCodes,
                selectedCountry: data.countryCodes.find(
                    (country: any) => country.code === countryCode.toLowerCase()
                ),
                // Set even on a failed match, so an unresolvable code doesn't spin forever.
                isCountryLookupSettled: true,
            });
        }

        stripeKey.current = (
            publicKeys.find(keySet => keySet.country === countryCode)?.key ??
            publicKeys.find(keySet => keySet.country === 'default')?.key
        ) || '';
    }, [data, appState.user?.CountryCodeFromIp, appState.country.codeFromIP, setAppState, appState.selectedPlan?.CountryCode]);

    useEffect(() => {
        if (appState.selectedPlan &&
            appState.selectedCountryCode &&
            appState.selectedPlan.CountryCode.toUpperCase() !== appState.selectedCountryCode.code.toUpperCase()
        ) {
            const countryCode = appState.countryCodes.find(
                (country: any) => country.code === appState.selectedPlan.CountryCode.toLowerCase()
            );
            setAppState({
                selectedCountry: countryCode
            })
            stripeKey.current = (
                publicKeys.find(keySet => keySet.country === countryCode?.code)?.key ??
                publicKeys.find(keySet => keySet.country === 'default')?.key
            ) || '';
        }
    }, [appState.countryCodes, appState.selectedCountryCode, appState.selectedPlan, setAppState]);

    const stripePromise: any = loadStripe(stripeKey.current);

    if (!stripePromise) {
        return <div />;
    }

    return (
        // key is necessary to ensure Stripe updates country on country change.
        <Elements key={stripeKey.current} stripe={stripePromise}>
            {children}
        </Elements>
    );
};

export default StripeLoader;
